import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { RegistrationForm, ConditionType } from '../../redux/types';
import { isValidEmail, isValidZip } from '../form/validation';
import { ReactFinalFormCheckBox, ReactFinalFormSelect, ReactFinalFormText } from './ReactFinalFormFields';

export const composeValidators = (...validators: any) => (value: any, fields?: any) => validators
  .reduce((error: any, validator: any) => error || validator(value, fields), undefined);
export const requiredField = (value: any) => (value ? '' : <FormattedMessage id="registration.required" />);
export const validEmail = (email: any) => (isValidEmail(email) ? '' : <FormattedMessage id="registration.noValidMail" />);
export const validZip = (zip: string) => (isValidZip(zip) ? '' : <FormattedMessage id="registration.noValidZip" />);

type ReactFinalFormWrapperProps = {
  registrationForm: RegistrationForm;
}

type ConditionProps = ConditionType & { children?: React.ReactChild }
const Condition: React.FC<ConditionProps> = ({
  when, is, reset, children,
}: ConditionProps) => {
  const formApi = useForm();
  const evaluation = (value: string | boolean) => {
    let hide = false;
    if (typeof is === 'boolean') {
      hide = value === is;
    } else if (Array.isArray(is) && typeof value !== 'boolean') {
      hide = is.indexOf(value) >= 0;
    } else {
      hide = value === is;
    }

    if (reset && hide) {
      formApi.change(reset, undefined);
    }

    return hide ? null : children;
  };
  return (
    <Field name={when} subscription={{ value: true }}>
      {({ input: { value } }) => evaluation(value)}
    </Field>
  );
};

const ReactFinalFormWrapper: React.FC<ReactFinalFormWrapperProps> = (
  props: ReactFinalFormWrapperProps,
) => {
  const { registrationForm } = props;
  const { fields } = registrationForm;

  const fieldsToRender = fields.map((field): React.ReactNode => {
    const {
      type,
      label,
      key,
      columnClassName,
      validate,
      conditional,
      defaultValue,
      disable,
      hide,
      placeholder,
    } = field;

    if (hide) {
      return null;
    }

    let fieldComponent;
    if (type === 'select') {
      const { options } = field;
      fieldComponent = (
        <ReactFinalFormSelect
          disable={disable}
          key={key}
          label={label}
          keyValue={key}
          columnClassName={columnClassName}
          type={type}
          defaultValue={defaultValue}
          options={options}
          validate={validate}
        />
      );
    }
    if (type === 'checkbox') {
      fieldComponent = (
        <ReactFinalFormCheckBox
          disable={disable}
          key={key}
          label={label}
          keyValue={key}
          columnClassName={columnClassName}
          type={type}
          defaultValue={defaultValue}
        />
      );
    }
    if (type === 'text'
      || type === 'password'
      || type === 'tel'
      || type === 'email') {
      fieldComponent = (
        <ReactFinalFormText
          disable={disable}
          key={key}
          label={label}
          keyValue={key}
          validate={validate}
          columnClassName={columnClassName}
          type={type}
          placeholder={placeholder}
        />
      );
    }
    if (type === 'info') {
      fieldComponent = (
        <div className="w-100">
          <p>{label}</p>
        </div>
      );
    }

    if (conditional) {
      const { when, is, reset } = conditional;
      return (
        <Condition key={`conditional-${key}`} when={when} is={is} reset={reset}>
          {fieldComponent}
        </Condition>
      );
    }
    return fieldComponent;
  });

  return <>{fieldsToRender}</>;
};

export default ReactFinalFormWrapper;
